import { useMemo, useState, useEffect, useCallback } from 'react';
import { useInput } from 'react-admin';
import { Box, Chip, Autocomplete, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { colorChoices } from '@/constants/TagCategoryColorChoices';
import { globalStyles } from '@/themes/commonStyles/global.js';
import { tagPickerStyles } from '@/themes/commonStyles/tagPicker.js';
import TagPickerPopup from '@/components/global/tags/TagPickerPopup.jsx';
import { useTagPickerState } from '@/composables/useTagPickerState.js';
import { useTagPicker } from '@/composables/useTagPicker';
import { useTagNameSource } from '@/i18n/hooks.js';

const StyledTagsInput = styled(Box)(({ theme, displayDeleteIcon }) => ({
    '& .tags-input': {
        minWidth: '100px',
        position: 'relative',
        '&.open': {
            ...tagPickerStyles.focusRingOutlined(theme),
        },
        '& .label-focused': {
            transform: 'translate(14px, -11px) scale(1)',
        },
        '& .MuiInputBase-input': {
            opacity: 1,
            minWidth: '120px',
            zIndex: '3',
            marginRight: '0 !important',
            marginLeft: '6px',
        },
        '&.mobile': {
            boxShadow: 'none',
            '& .tags-input__popup': {
                position: 'relative',
                maxHeight: 'fit-content',
                overflow: 'hidden',
            },
        },
        '& .MuiAutocomplete-root': {
            ...globalStyles.arrayFilterAutocompleteInput,
            '& .MuiFormControl-root': {
                '&::before': {
                    content: 'none',
                },
                '&::after': {
                    content: 'none',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    width: '100%',
                    overflow: 'visible',
                    padding: '12px 16px !important',
                    minWidth: '276px',
                    maxWidth: '100%',
                    cursor: 'auto',
                    '& input[type="text"]': {
                        minWidth: '0px',
                        zIndex: '3',
                        marginRight: '0 !important',
                        marginLeft: '6px',
                    },
                },
            },
            '& .MuiChip-root': {
                zIndex: '2',
                cursor: 'auto',
                padding: '4px 5px 4px 8px',
                borderRadius: '8px',
                '& .MuiChip-label': {
                    padding: '0 2px 0 0',
                },
                '& .MuiChip-deleteIcon': {
                    display: displayDeleteIcon ? 'block' : 'none',
                },
            },
        },

        '& .tags-input__popup': {
            ...tagPickerStyles.popupContainer,
            [theme.breakpoints.down('md')]: {
                minWidth: 'unset',
            },
            '& .loading': {
                ...globalStyles.filterLoading,
                margin: '10px auto',
            },
            '& .MuiBox-root': {
                width: 'auto !important',
            },
        },
        '& .tags-input__categories': {
            ...tagPickerStyles.categoriesList,
            [theme.breakpoints.down('md')]: {
                padding: '24px',
            },
        },
    },
}));

export const TagArrayInput = ({ source, label, searchFields = ['name'], filter = {}, displayDeleteIcon = true, forceDesktopOnMobile = false }) => {
    const tagNameSource = useTagNameSource();
    const { field } = useInput({ source });
    const { tags, groupedTags, isLoading, mobileMode, toggleAllSelection, setSearchText } = useTagPicker({
        searchFields,
        baseFilter: filter,
        forceDesktopOnMobile,
    });
    const onApply = ids => {
        field.onChange(ids);
    };
    const { open, setOpen, selectedIds, setSelectedIds, tempSelectedIds, setTempSelectedIds, handleTagToggle, handleApply, containerRef, popupRef } =
        useTagPickerState({ initialSelectedIds: field.value || [], onApply, mobileMode });
    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [allSelectedTags, setAllSelectedTags] = useState([]);

    useEffect(() => {
        if (!open) {
            setSearchText('');
            setInputValue('');
            setFocused(false);
        } else {
            setFocused(true);
        }
    }, [open, setSearchText]);

    useEffect(() => {
        if (!selectedIds || selectedIds.length === 0) {
            setAllSelectedTags([]);
            return;
        }
        const currentSelectedTags = (tags || []).filter(t => selectedIds.includes(t.id));
        setAllSelectedTags(prevTags => {
            const filteredPrevTags = prevTags.filter(tag => selectedIds.includes(tag.id));
            const newTags = currentSelectedTags.filter(tag => !filteredPrevTags.some(existingTag => existingTag.id === tag.id));

            return [...filteredPrevTags, ...newTags];
        });
    }, [selectedIds, tags]);

    const handleToggleAll = useCallback(() => {
        const newSelection = toggleAllSelection(tempSelectedIds);
        setTempSelectedIds(newSelection);
    }, [tempSelectedIds, toggleAllSelection, setTempSelectedIds]);

    const selectedTags = useMemo(() => {
        return allSelectedTags.filter(tag => selectedIds.includes(tag.id));
    }, [allSelectedTags, selectedIds]);

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
        setSearchText(newInputValue);
    };

    const handleDeleteTag = useCallback(
        (event, tagId) => {
            event?.stopPropagation?.();
            const next = (selectedIds || []).filter(id => id !== tagId);
            setSelectedIds(next);
            setTempSelectedIds(next);
            setAllSelectedTags(prevTags => prevTags.filter(tag => tag.id !== tagId));
            field.onChange(next);
        },
        [field, selectedIds, setSelectedIds, setTempSelectedIds],
    );

    const renderTags = useCallback(
        value =>
            value.map(tag => {
                const colorChoice = colorChoices.find(choice => choice.id === tag.category?.color);
                const backgroundColor = colorChoice?.shades?.[200] || tag.category?.color;
                const chipLabel = tag[tagNameSource] ?? tag.name;

                return <Chip key={tag.id} label={chipLabel} size='small' style={{ backgroundColor }} onDelete={event => handleDeleteTag(event, tag.id)} />;
            }),
        [handleDeleteTag, tagNameSource],
    );

    const handleOpen = () => setOpen(true);

    const renderInput = params => {
        const hasSelectedTags = Boolean(selectedIds?.length);
        return (
            <TextField
                {...params}
                label={label}
                focused={focused}
                onClick={handleOpen}
                onChange={event => handleInputChange(event, event.target.value)}
                InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: open || hasSelectedTags || Boolean(inputValue) || params.InputLabelProps?.shrink,
                    className: `${params.InputLabelProps?.className || ''} ${hasSelectedTags ? 'label-focused' : ''}`.trim(),
                }}
            />
        );
    };

    return (
        <StyledTagsInput ref={containerRef} displayDeleteIcon={displayDeleteIcon}>
            <Box className={`tags-input ${open ? 'open' : ''} ${mobileMode ? 'mobile' : ''}`}>
                <Autocomplete
                    multiple
                    disablePortal
                    options={[]}
                    value={selectedTags}
                    inputValue={inputValue}
                    open={false}
                    onOpen={handleOpen}
                    onInputChange={handleInputChange}
                    freeSolo
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                        }
                    }}
                    renderTags={renderTags}
                    renderInput={renderInput}
                />
                <TagPickerPopup
                    open={open}
                    isLoading={isLoading}
                    tags={tags}
                    groupedTags={groupedTags}
                    tempSelectedIds={tempSelectedIds}
                    selectedIds={selectedIds}
                    setOpen={setOpen}
                    setTempSelectedIds={setTempSelectedIds}
                    handleTagToggle={handleTagToggle}
                    handleToggleAll={handleToggleAll}
                    handleApply={handleApply}
                    popupRef={popupRef}
                    categoriesClassName='tags-input__categories'
                    containerClassName='tags-input__popup'
                    actionsDisabled={isLoading}
                />
            </Box>
        </StyledTagsInput>
    );
};

TagArrayInput.propTypes = {
    source: PropTypes.string,
    label: PropTypes.string,
    displayDeleteIcon: PropTypes.bool,
    forceDesktopOnMobile: PropTypes.bool,
    filter: PropTypes.object,
    searchFields: PropTypes.arrayOf(PropTypes.string),
};

export default TagArrayInput;
